/** Anchoring a comment to text on the page, as a Google Docs comment is: the selected
 * text plus a little of what surrounds it, found again later by matching. Matching
 * ignores whitespace, because a selection's text puts line breaks between blocks that
 * the DOM's text nodes do not carry. See docs/Y2026-Q4/design-review-mode.md. */

/** How much text either side of a quote is kept to tell repeats of it apart. */
export const CONTEXT_CHARS = 40;

const WS = /\s/;

interface TextIndex {
  /** The root's text with all whitespace removed. */
  text: string;
  /** For each character of ``text``, the text node and offset it came from. */
  at: { node: Text; offset: number }[];
}

const indexText = (root: Node): TextIndex => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let text = "";
  const at: TextIndex["at"] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const t = node as Text;
    // Controls the review adds are not part of the document.
    if (t.parentElement?.closest("[data-review-chrome]")) continue;
    const data = t.data;
    for (let i = 0; i < data.length; i++) {
      if (!WS.test(data[i])) {
        text += data[i];
        at.push({ node: t, offset: i });
      }
    }
  }
  return { text, at };
};

const squash = (s: string): string => s.replace(/\s+/g, "");

/** How many characters ``a`` and ``b`` share from their ends (``fromEnd``) or starts. */
const overlap = (a: string, b: string, fromEnd: boolean): number => {
  let n = 0;
  while (n < a.length && n < b.length) {
    const ca = fromEnd ? a[a.length - 1 - n] : a[n];
    const cb = fromEnd ? b[b.length - 1 - n] : b[n];
    if (ca !== cb) break;
    n++;
  }
  return n;
};

/** The range ``quote`` occupies under ``root``, preferring the occurrence whose
 * surroundings best match ``prefix`` and ``suffix``; null when it is no longer there. */
export const findQuote = (
  root: Node,
  quote: string,
  prefix: string,
  suffix: string,
): Range | null => {
  const q = squash(quote);
  if (!q) return null;
  const { text, at } = indexText(root);
  const before = squash(prefix);
  const after = squash(suffix);
  let best = -1;
  let bestScore = -1;
  for (let i = text.indexOf(q); i !== -1; i = text.indexOf(q, i + 1)) {
    const score =
      overlap(text.slice(Math.max(0, i - before.length), i), before, true) +
      overlap(text.slice(i + q.length, i + q.length + after.length), after, false);
    if (score > bestScore) {
      best = i;
      bestScore = score;
    }
  }
  if (best === -1) return null;
  const start = at[best];
  const end = at[best + q.length - 1];
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
};

/** The selected text and what surrounds it inside ``root``. */
export const quoteOf = (
  range: Range,
  root: Node,
): { quote: string; prefix: string; suffix: string } => {
  const head = document.createRange();
  head.selectNodeContents(root);
  head.setEnd(range.startContainer, range.startOffset);
  const tail = document.createRange();
  tail.selectNodeContents(root);
  tail.setStart(range.endContainer, range.endOffset);
  const tidy = (s: string) => s.replace(/\s+/g, " ");
  return {
    quote: tidy(range.toString()).trim(),
    prefix: tidy(head.toString()).slice(-CONTEXT_CHARS),
    suffix: tidy(tail.toString()).slice(0, CONTEXT_CHARS),
  };
};

/** The text position under a click, in either browser API. */
export const pointAt = (x: number, y: number): { node: Node; offset: number } | null => {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    return p ? { node: p.offsetNode, offset: p.offset } : null;
  }
  const r = document.caretRangeFromPoint?.(x, y);
  return r ? { node: r.startContainer, offset: r.startOffset } : null;
};
